/**
 * Declarative transformation-op registry.
 *
 * Each op declares its metadata, a typed config schema (so the UI can generate a real form
 * with column-aware pickers instead of hand-coded JSX per op), and a pure `apply` function.
 * The pipeline engine (`customTileTransformUtils.ts`) dispatches to `apply` by `type`; the
 * modal renders `configFields` via `ConfigFieldForm`. Adding an op is now a single entry
 * here — no switch statements, no bespoke form code.
 *
 * Config-driven calculations use the sandboxed evaluator in `safeExpression.ts`, never
 * `eval()`/`new Function`. Raw JavaScript remains available only through the modal's
 * clearly-labeled "Advanced" script tab (the `script` type is handled by the engine
 * directly and is intentionally NOT in this registry).
 */
import { compileExpression, validateExpression } from './safeExpression';

export type ConfigFieldType =
  | 'column'      // single column picker (dropdown of detected columns + free text)
  | 'columns'     // multi-column picker
  | 'string'      // free text
  | 'number'      // numeric input
  | 'select'      // fixed set of options
  | 'boolean'     // checkbox
  | 'expression'; // sandboxed formula (validated against safeExpression)

export interface ConfigField {
  key: string;
  label: string;
  type: ConfigFieldType;
  required?: boolean;
  default?: any;
  placeholder?: string;
  helperText?: string;
  options?: { value: string; label: string }[]; // for `select`
}

export interface TransformationOp {
  type: string;
  label: string;
  description: string;
  category: string;
  /** When true, `apply` receives an array-of-arrays (one flat row array per selected source). */
  multiSource?: boolean;
  configFields: ConfigField[];
  /**
   * `data` is a flat array of row objects, unless `multiSource` is true (array-of-arrays).
   * Return value is coerced to an array of objects by the engine.
   */
  apply: (config: any, data: any[]) => any;
  /** Short human-readable summary of a given config, for list rows. */
  summary?: (config: any) => string;
}

// --- helpers shared by ops ---

const asRows = (data: any): any[] => (Array.isArray(data) ? data : []);
const num = (v: any): number => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : NaN;
};

const AGGREGATIONS: { value: string; label: string }[] = [
  { value: 'count', label: 'Count' },
  { value: 'sum', label: 'Sum' },
  { value: 'avg', label: 'Average' },
  { value: 'min', label: 'Min' },
  { value: 'max', label: 'Max' },
];

function aggregate(op: string, values: any[]): number {
  const nums = values.map(num).filter((n) => Number.isFinite(n));
  switch (op) {
    case 'count': return values.length;
    case 'sum': return nums.reduce((a, b) => a + b, 0);
    case 'avg': return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;
    case 'min': return nums.length ? Math.min(...nums) : 0;
    case 'max': return nums.length ? Math.max(...nums) : 0;
    default: return values.length;
  }
}

// --- The op catalog ---

const OPS: TransformationOp[] = [
  // ---------- Combine (multi-source) ----------
  {
    type: 'combine',
    label: 'Combine sources',
    description: 'Concatenate the rows of every selected source into a single list.',
    category: 'Combine',
    multiSource: true,
    configFields: [],
    apply: (_config, data) => (Array.isArray(data) ? data.flat() : []),
    summary: () => 'Concatenate all selected sources',
  },
  {
    type: 'joinByField',
    label: 'Join by field',
    description: 'Left-join the second source onto the first, matching rows on a shared key column.',
    category: 'Combine',
    multiSource: true,
    configFields: [
      { key: 'joinField', label: 'Field to join on', type: 'column', required: true },
    ],
    apply: (config, data) => {
      if (!Array.isArray(data) || data.length < 2 || !Array.isArray(data[0]) || !Array.isArray(data[1])) {
        return Array.isArray(data) ? data.flat() : [];
      }
      const [left, right] = data;
      const key = config.joinField;
      return left.map((l: any) => {
        const match = right.find((r: any) => r?.[key] === l?.[key]);
        return match ? { ...l, ...match } : { ...l };
      });
    },
    summary: (c) => `Join on "${c?.joinField ?? '?'}"`,
  },
  {
    type: 'mergeSeries',
    label: 'Merge series',
    description: 'Merge the sources positionally — row N of each source is combined into one row.',
    category: 'Combine',
    multiSource: true,
    configFields: [],
    apply: (_config, data) => {
      if (!Array.isArray(data) || !data.length || !Array.isArray(data[0])) {
        return Array.isArray(data) ? data.flat() : [];
      }
      const sources = data as any[][];
      const maxLen = Math.max(...sources.map((s) => (Array.isArray(s) ? s.length : 0)));
      const out: any[] = [];
      for (let i = 0; i < maxLen; i++) {
        out.push(sources.reduce((acc, s) => ({ ...acc, ...(s?.[i] || {}) }), {}));
      }
      return out;
    },
    summary: () => 'Merge sources by position',
  },

  // ---------- Filter ----------
  {
    type: 'filter',
    label: 'Filter rows',
    description: 'Keep only rows where a column satisfies a comparison.',
    category: 'Filter',
    configFields: [
      { key: 'filterField', label: 'Field to filter', type: 'column', required: true },
      {
        key: 'operator', label: 'Operator', type: 'select', default: 'equals',
        options: [
          { value: 'equals', label: '= equals' },
          { value: 'notEquals', label: '≠ not equals' },
          { value: 'contains', label: 'contains' },
          { value: 'gt', label: '> greater than' },
          { value: 'gte', label: '≥ greater or equal' },
          { value: 'lt', label: '< less than' },
          { value: 'lte', label: '≤ less or equal' },
        ],
      },
      { key: 'filterValue', label: 'Value', type: 'string', required: true },
    ],
    apply: (config, data) => {
      const { filterField, operator = 'equals', filterValue } = config;
      return asRows(data).filter((row) => {
        const cell = row?.[filterField];
        switch (operator) {
          case 'notEquals': return String(cell) !== String(filterValue);
          case 'contains': return String(cell ?? '').includes(String(filterValue ?? ''));
          case 'gt': return num(cell) > num(filterValue);
          case 'gte': return num(cell) >= num(filterValue);
          case 'lt': return num(cell) < num(filterValue);
          case 'lte': return num(cell) <= num(filterValue);
          case 'equals':
          default: return String(cell) === String(filterValue);
        }
      });
    },
    summary: (c) => `Keep where "${c?.filterField ?? '?'}" ${c?.operator ?? 'equals'} "${c?.filterValue ?? ''}"`,
  },
  {
    type: 'limit',
    label: 'Limit rows',
    description: 'Keep only the first N rows.',
    category: 'Filter',
    configFields: [
      { key: 'limit', label: 'Number of rows', type: 'number', required: true, default: 10 },
    ],
    apply: (config, data) => asRows(data).slice(0, Number(config.limit) || 0),
    summary: (c) => `First ${c?.limit ?? '?'} rows`,
  },
  {
    type: 'filterFieldsByName',
    label: 'Drop columns by pattern',
    description: 'Remove every column whose name matches a regular expression.',
    category: 'Filter',
    configFields: [
      { key: 'regex', label: 'Column-name pattern (regex)', type: 'string', required: true, placeholder: 'e.g. ^_|Id$' },
    ],
    apply: (config, data) => {
      let re: RegExp | null = null;
      try { re = config.regex ? new RegExp(config.regex) : null; } catch { re = null; }
      return asRows(data).map((row) =>
        Object.fromEntries(Object.entries(row).filter(([k]) => !(re && re.test(k)))),
      );
    },
    summary: (c) => `Drop columns matching /${c?.regex ?? ''}/`,
  },

  // ---------- Fields ----------
  {
    type: 'reorderRename',
    label: 'Rename a column',
    description: 'Rename one column to a new name, leaving its values unchanged.',
    category: 'Fields',
    configFields: [
      { key: 'oldName', label: 'Existing column', type: 'column', required: true },
      { key: 'newName', label: 'New name', type: 'string', required: true },
    ],
    apply: (config, data) =>
      asRows(data).map((row) => {
        if (!Object.prototype.hasOwnProperty.call(row, config.oldName)) return row;
        const { [config.oldName]: oldValue, ...rest } = row;
        return { ...rest, [config.newName || 'renamed']: oldValue };
      }),
    summary: (c) => `Rename "${c?.oldName ?? '?'}" → "${c?.newName ?? '?'}"`,
  },
  {
    type: 'calculateNewFields',
    label: 'Calculate new field',
    description: 'Add a column computed from a formula. Reference columns as row.field (e.g. row.price * row.qty).',
    category: 'Fields',
    configFields: [
      { key: 'newFieldName', label: 'New field name', type: 'string', required: true },
      {
        key: 'calculation', label: 'Formula', type: 'expression', required: true,
        placeholder: 'row.price * row.qty',
        helperText: 'Use row.field for column values. Supports + - * / %, comparisons, && || !, ?: and Math.*',
      },
    ],
    apply: (config, data) => {
      const name = config.newFieldName || 'newField';
      const expr = config.calculation || '';
      let compiled: (scope: Record<string, any>) => any;
      try {
        compiled = compileExpression(expr);
      } catch (e) {
        console.warn('calculateNewFields: invalid formula', expr, e);
        return asRows(data);
      }
      return asRows(data).map((row) => {
        let value: any;
        try { value = compiled({ row, ...row }); } catch { value = undefined; }
        return { ...row, [name]: value };
      });
    },
    summary: (c) => `${c?.newFieldName ?? 'newField'} = ${c?.calculation ?? ''}`,
  },
  {
    type: 'convertFieldType',
    label: 'Convert column type',
    description: 'Cast a column to number, string, or boolean.',
    category: 'Fields',
    configFields: [
      { key: 'fieldName', label: 'Column', type: 'column', required: true },
      {
        key: 'targetType', label: 'Convert to', type: 'select', default: 'number',
        options: [
          { value: 'number', label: 'Number' },
          { value: 'string', label: 'String' },
          { value: 'boolean', label: 'Boolean' },
        ],
      },
    ],
    apply: (config, data) => {
      const { fieldName, targetType = 'number' } = config;
      return asRows(data).map((row) => {
        if (!(fieldName in row)) return row;
        const v = row[fieldName];
        let converted: any = v;
        if (targetType === 'number') converted = num(v);
        else if (targetType === 'string') converted = v == null ? '' : String(v);
        else if (targetType === 'boolean') converted = v === true || v === 'true' || v === 1 || v === '1';
        return { ...row, [fieldName]: converted };
      });
    },
    summary: (c) => `"${c?.fieldName ?? '?'}" → ${c?.targetType ?? 'number'}`,
  },
  {
    type: 'concatenateFields',
    label: 'Concatenate columns',
    description: 'Join several columns into a single new column with a separator.',
    category: 'Fields',
    configFields: [
      { key: 'fields', label: 'Columns to combine', type: 'columns', required: true },
      { key: 'targetField', label: 'New column name', type: 'string', required: true, default: 'combined' },
      { key: 'separator', label: 'Separator', type: 'string', default: ' ' },
    ],
    apply: (config, data) => {
      const fields: string[] = Array.isArray(config.fields) ? config.fields : [];
      const target = config.targetField || 'combined';
      const sep = config.separator ?? ' ';
      return asRows(data).map((row) => ({
        ...row,
        [target]: fields.map((f) => row?.[f] ?? '').join(sep),
      }));
    },
    summary: (c) => `${(c?.fields || []).join(' + ')} → "${c?.targetField ?? 'combined'}"`,
  },
  {
    type: 'formatString',
    label: 'Format text column',
    description: 'Transform a text column: uppercase, lowercase, trim, or capitalize.',
    category: 'Fields',
    configFields: [
      { key: 'field', label: 'Column', type: 'column', required: true },
      {
        key: 'transform', label: 'Transform', type: 'select', default: 'upper',
        options: [
          { value: 'upper', label: 'UPPERCASE' },
          { value: 'lower', label: 'lowercase' },
          { value: 'trim', label: 'Trim whitespace' },
          { value: 'capitalize', label: 'Capitalize Words' },
        ],
      },
    ],
    apply: (config, data) => {
      const { field, transform = 'upper' } = config;
      return asRows(data).map((row) => {
        if (!(field in row) || row[field] == null) return row;
        const s = String(row[field]);
        let out = s;
        if (transform === 'upper') out = s.toUpperCase();
        else if (transform === 'lower') out = s.toLowerCase();
        else if (transform === 'trim') out = s.trim();
        else if (transform === 'capitalize') out = s.replace(/\b\w/g, (c) => c.toUpperCase());
        return { ...row, [field]: out };
      });
    },
    summary: (c) => `${c?.transform ?? 'upper'} "${c?.field ?? '?'}"`,
  },
  {
    type: 'extractFields',
    label: 'Parse JSON column',
    description: 'Parse a column that holds a JSON string and merge its keys into each row.',
    category: 'Fields',
    configFields: [
      { key: 'sourceField', label: 'JSON column', type: 'column', required: true },
    ],
    apply: (config, data) => {
      const src = config.sourceField;
      return asRows(data).map((row) => {
        const raw = row?.[src];
        if (typeof raw !== 'string') return row;
        try {
          const parsed = JSON.parse(raw);
          return parsed && typeof parsed === 'object' ? { ...row, ...parsed } : row;
        } catch {
          return row;
        }
      });
    },
    summary: (c) => `Parse JSON in "${c?.sourceField ?? '?'}"`,
  },
  {
    type: 'renameFieldsByRegex',
    label: 'Rename columns by pattern',
    description: 'Rewrite every column name by replacing a regex match.',
    category: 'Fields',
    configFields: [
      { key: 'regex', label: 'Match (regex)', type: 'string', required: true },
      { key: 'replaceWith', label: 'Replace with', type: 'string', default: '' },
    ],
    apply: (config, data) => {
      let re: RegExp | null = null;
      try { re = config.regex ? new RegExp(config.regex, 'g') : null; } catch { re = null; }
      if (!re) return asRows(data);
      const rep = config.replaceWith ?? '';
      return asRows(data).map((row) =>
        Object.fromEntries(Object.entries(row).map(([k, v]) => [k.replace(re as RegExp, rep), v])),
      );
    },
    summary: (c) => `Rename /${c?.regex ?? ''}/ → "${c?.replaceWith ?? ''}"`,
  },
  {
    type: 'sortBy',
    label: 'Sort rows',
    description: 'Order rows by a column, ascending or descending.',
    category: 'Fields',
    configFields: [
      { key: 'fieldName', label: 'Sort by column', type: 'column', required: true },
      {
        key: 'direction', label: 'Direction', type: 'select', default: 'asc',
        options: [{ value: 'asc', label: 'Ascending' }, { value: 'desc', label: 'Descending' }],
      },
    ],
    apply: (config, data) => {
      const { fieldName, direction = 'asc' } = config;
      const dir = direction === 'desc' ? -1 : 1;
      return [...asRows(data)].sort((a, b) => {
        const av = a?.[fieldName];
        const bv = b?.[fieldName];
        const an = num(av);
        const bn = num(bv);
        if (Number.isFinite(an) && Number.isFinite(bn)) return (an - bn) * dir;
        return String(av ?? '').localeCompare(String(bv ?? '')) * dir;
      });
    },
    summary: (c) => `Sort by "${c?.fieldName ?? '?'}" ${c?.direction ?? 'asc'}`,
  },

  // ---------- Aggregate & reshape ----------
  {
    type: 'groupBy',
    label: 'Group & aggregate',
    description: 'Group rows by a column and aggregate a value column, producing one row per group.',
    category: 'Aggregate & Reshape',
    configFields: [
      { key: 'groupField', label: 'Group by column', type: 'column', required: true },
      {
        key: 'aggregation', label: 'Aggregation', type: 'select', default: 'count',
        options: AGGREGATIONS,
      },
      { key: 'valueField', label: 'Value column', type: 'column', helperText: 'Required for Sum/Average/Min/Max' },
      { key: 'outputField', label: 'Output column name', type: 'string', default: 'value' },
    ],
    apply: (config, data) => {
      const { groupField, aggregation = 'count', valueField, outputField = 'value' } = config;
      const groups = new Map<any, any[]>();
      for (const row of asRows(data)) {
        const key = row?.[groupField];
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(row);
      }
      return Array.from(groups.entries()).map(([key, rows]) => {
        const values = aggregation === 'count' ? rows : rows.map((r) => r?.[valueField]);
        return { [groupField]: key, [outputField]: aggregate(aggregation, values) };
      });
    },
    summary: (c) => `Group by "${c?.groupField ?? '?'}", ${c?.aggregation ?? 'count'}${c?.valueField ? ` of "${c.valueField}"` : ''}`,
  },
  {
    type: 'reduce',
    label: 'Aggregate all rows',
    description: 'Collapse all rows to a single value by aggregating one column.',
    category: 'Aggregate & Reshape',
    configFields: [
      {
        key: 'aggregation', label: 'Aggregation', type: 'select', default: 'sum',
        options: AGGREGATIONS,
      },
      { key: 'valueField', label: 'Value column', type: 'column', helperText: 'Required for Sum/Average/Min/Max' },
      { key: 'outputField', label: 'Output column name', type: 'string', default: 'value' },
    ],
    apply: (config, data) => {
      const { aggregation = 'sum', valueField, outputField = 'value' } = config;
      const rows = asRows(data);
      const values = aggregation === 'count' ? rows : rows.map((r) => r?.[valueField]);
      return [{ [outputField]: aggregate(aggregation, values) }];
    },
    summary: (c) => `${c?.aggregation ?? 'sum'}${c?.valueField ? ` of "${c.valueField}"` : ''}`,
  },
  {
    type: 'histogram',
    label: 'Histogram (bin values)',
    description: 'Bucket a numeric column into fixed-width bins and count rows per bin.',
    category: 'Aggregate & Reshape',
    configFields: [
      { key: 'fieldName', label: 'Numeric column', type: 'column', required: true },
      { key: 'binSize', label: 'Bin size', type: 'number', required: true, default: 100 },
    ],
    apply: (config, data) => {
      const { fieldName } = config;
      const binSize = Number(config.binSize) || 1;
      const bins: Record<number, number> = {};
      for (const row of asRows(data)) {
        const v = num(row?.[fieldName]);
        if (!Number.isFinite(v)) continue;
        const bin = Math.floor(v / binSize) * binSize;
        bins[bin] = (bins[bin] || 0) + 1;
      }
      return Object.entries(bins)
        .map(([bin, count]) => ({ bin: Number(bin), count }))
        .sort((a, b) => a.bin - b.bin);
    },
    summary: (c) => `Histogram of "${c?.fieldName ?? '?'}" (bin ${c?.binSize ?? '?'})`,
  },
  {
    type: 'rowsToFields',
    label: 'Rows to a single record',
    description: 'Pivot key/value rows into one wide record — each row becomes a column.',
    category: 'Aggregate & Reshape',
    configFields: [
      { key: 'keyField', label: 'Column name source', type: 'column', required: true },
      { key: 'valueField', label: 'Value source', type: 'column', required: true },
    ],
    apply: (config, data) => {
      const { keyField, valueField } = config;
      const record: Record<string, any> = {};
      for (const row of asRows(data)) {
        if (row?.[keyField] == null) continue;
        record[String(row[keyField])] = row?.[valueField];
      }
      return [record];
    },
    summary: (c) => `Pivot "${c?.keyField ?? '?'}" → columns`,
  },
  {
    type: 'labelsToFields',
    label: 'Pivot long to wide',
    description: 'Turn a label column into separate columns, grouped by another column (great for stacked bars).',
    category: 'Aggregate & Reshape',
    configFields: [
      { key: 'groupField', label: 'Row group column', type: 'column', required: true },
      { key: 'labelField', label: 'Label column (becomes new columns)', type: 'column', required: true },
      { key: 'valueField', label: 'Value column', type: 'column', required: true },
    ],
    apply: (config, data) => {
      const { groupField, labelField, valueField } = config;
      const groups = new Map<any, any>();
      for (const row of asRows(data)) {
        const g = row?.[groupField];
        if (!groups.has(g)) groups.set(g, { [groupField]: g });
        const label = row?.[labelField];
        if (label != null) groups.get(g)[String(label)] = row?.[valueField];
      }
      return Array.from(groups.values());
    },
    summary: (c) => `Pivot "${c?.labelField ?? '?'}" across "${c?.groupField ?? '?'}"`,
  },
  {
    type: 'prepareTimeSeries',
    label: 'Prepare time series',
    description: 'Map columns to a standard { time, name, value } shape for time-series charts.',
    category: 'Aggregate & Reshape',
    configFields: [
      { key: 'timeField', label: 'Time column', type: 'column', required: true },
      { key: 'valueField', label: 'Value column', type: 'column', required: true },
      { key: 'nameField', label: 'Series name column', type: 'column' },
    ],
    apply: (config, data) => {
      const { timeField, valueField, nameField } = config;
      return asRows(data).map((row) => ({
        time: row?.[timeField],
        ...(nameField ? { name: row?.[nameField] } : {}),
        value: num(row?.[valueField]),
      }));
    },
    summary: (c) => `Time series on "${c?.timeField ?? '?'}"`,
  },
  {
    type: 'transpose',
    label: 'Transpose',
    description: 'Flip rows and columns — each original column becomes a row.',
    category: 'Aggregate & Reshape',
    configFields: [],
    apply: (_config, data) => {
      const rows = asRows(data);
      if (!rows.length) return [];
      return Object.keys(rows[0]).map((key) =>
        Object.fromEntries(rows.map((row, j) => [String(j), row?.[key]])),
      );
    },
    summary: () => 'Transpose rows/columns',
  },
];

// --- lookups ---

const OP_BY_TYPE: Record<string, TransformationOp> = Object.fromEntries(OPS.map((op) => [op.type, op]));

export const TRANSFORMATION_OPS = OPS;

export function getOp(type: string): TransformationOp | undefined {
  return OP_BY_TYPE[type];
}

export function isMultiSourceOp(type: string): boolean {
  return !!OP_BY_TYPE[type]?.multiSource;
}

/** Op types grouped by category, in registry order — for grouped pickers. */
export function getOpsByCategory(): { category: string; ops: TransformationOp[] }[] {
  const order: string[] = [];
  const byCat = new Map<string, TransformationOp[]>();
  for (const op of OPS) {
    if (!byCat.has(op.category)) { byCat.set(op.category, []); order.push(op.category); }
    byCat.get(op.category)!.push(op);
  }
  return order.map((category) => ({ category, ops: byCat.get(category)! }));
}

export { validateExpression };
